// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductDetailsVimeo from "@conklin/App/Components/ProductDetailsVimeo/ProductDetailsVimeo";
import { CustomAppState } from "@conklin/App/Store/Reducers";
import DataTable from "@conklin/MobiusOverrides/DataTable/DataTable";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { AttributeTypeModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import { DataTableBody, DataTableCell, DataTableRow } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    showDocuments = "showDocuments",
    showAttributes = "showAttributes",
    attributesPosition = "attributesPosition",
    documentsPosition = "documentsPosition",
    // XNG-Changes
    isVideoExpandedByDefault = "isVideoExpandedByDefault",
}

type Props = WidgetProps & HasProduct;

export interface ProductDetailsSpecificationsStyles {
    specificationsAccordion?: AccordionProps;
    specificationsAccordionSection?: AccordionSectionPresentationProps;
    documentsContainer?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
    attributeTypeWrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    // XNG-Change
    table?: InjectableCss;
    tableContainer?: InjectableCss;
    label?: TypographyPresentationProps;
    value?: TypographyPresentationProps;
    valueCell?: InjectableCss;
    labelCell?: InjectableCss;
    tablesWrapper?: InjectableCss;
}

export const specificationsStyles: ProductDetailsSpecificationsStyles = {
    specificationsAccordion: {
        headingLevel: 2,
    },
    documentGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    documentIcon: {
        src: File,
        color: "text.link",
        css: css`
            margin-right: 10px;
        `,
    },
    documentNameText: {
        css: css`
            vertical-align: super;
        `,
    },
    attributeTypeLabelText: {
        weight: "bold",
    },
};

const styles = specificationsStyles;

const ProductDetailsSpecifications: React.FC<Props> = ({
    product: { brand, attributeTypes, documents, specifications, content },
    fields: {
        showDocuments,
        showAttributes,
        documentsPosition,
        attributesPosition,
        // XNG-Changes
        isVideoExpandedByDefault,
    },
}) => {
    const renderingDocuments = showDocuments && documents && documents.length > 0;
    const renderingDescription = content && content.htmlContent;
    const renderingAttributes = showAttributes && (brand || (attributeTypes && attributeTypes?.length > 0));
    const renderingSpecifications = specifications && specifications.length > 0;
    const documentsFirst = documentsPosition === "first";
    const attributesFirst = attributesPosition === "first";
    // xng-change
    const isTabletScreen = useAppSelector(state => (state as CustomAppState).xCommon.isTabletScreen);

    // XNG-Change: display description
    if (!renderingDocuments && !renderingAttributes && !renderingSpecifications && !renderingDescription) {
        return null;
    }

    const documentsAccordion = renderingDocuments && (
        <ManagedAccordionSection
            title={translate("Documents")}
            data-test-selector="productDetails_specifications_documents"
            initialExpanded={
                true
                // ((!attributesFirst || !renderingAttributes) && documentsFirst) ||
                // (!renderingSpecifications && !renderingAttributes)
            }
            {...styles.specificationsAccordionSection}
        >
            <GridContainer {...styles.documentsContainer}>
                {documents!.map(document => (
                    <GridItem key={document.id.toString()} {...styles.documentGridItem}>
                        <Link
                            href={document.filePath}
                            target="_new"
                            {...styles.documentLink}
                            aria-label={document.name}
                            data-test-selector={`productDetails_${document.id}`}
                        >
                            <IconMemo {...styles.documentIcon} />
                            <Typography {...styles.documentNameText}>{document.name}</Typography>
                        </Link>
                    </GridItem>
                ))}
            </GridContainer>
        </ManagedAccordionSection>
    );
    // XNG-Change
    const descriptionAccordion = renderingDescription && (
        <ManagedAccordionSection
            title={translate("Description")}
            data-test-selector="productDetails_specifications_description"
            initialExpanded={
                true
                // (!documentsFirst || !renderingDocuments) && (!attributesFirst || !renderingAttributes)
            }
            {...styles.specificationsAccordionSection}
        >
            <div data-test-selector="productDetails_htmlContent">
                {parse(transformContentHtml(content.htmlContent), parserOptions)}
            </div>
        </ManagedAccordionSection>
    );

    // XNG-Changes
    const attributesFirstTable: any[] = [];
    const attributesSecondTable: any[] = [];

    renderingAttributes &&
        brand &&
        attributesFirstTable.push(
            // XNG-Changes: key
            <DataTableRow key={`brand-${brand.name}`}>
                <DataTableCell {...styles.labelCell}>
                    <Typography {...styles.label}>{translate("Brand")}</Typography>
                </DataTableCell>
                <DataTableCell {...styles.valueCell}>
                    <Typography {...styles.value}>{brand.name}</Typography>
                </DataTableCell>
            </DataTableRow>,
        );

    const pushRow = (rowsArray: any[], attributeType: AttributeTypeModel) => {
        rowsArray.push(
            <DataTableRow key={attributeType.id.toString()}>
                <DataTableCell {...styles.labelCell}>
                    <Typography {...styles.label}>{attributeType.label || attributeType.name}</Typography>
                </DataTableCell>
                <DataTableCell {...styles.valueCell}>
                    <Typography {...styles.value}>
                        {attributeType.attributeValues!.map(attributeValue => attributeValue.valueDisplay).join(", ")}
                    </Typography>
                </DataTableCell>
            </DataTableRow>,
        );
    };

    renderingAttributes &&
        attributeTypes &&
        attributeTypes.forEach((attributeType, index) => {
            brand
                ? index % 2
                    ? pushRow(attributesFirstTable, attributeType)
                    : pushRow(attributesSecondTable, attributeType)
                : index % 2
                  ? pushRow(attributesSecondTable, attributeType)
                  : pushRow(attributesFirstTable, attributeType);
        });

    const attrFirstTableLengthIsOdd =
        attributesFirstTable && attributesFirstTable.length && attributesFirstTable.length % 2;

    // End of XNG-Changes

    const attributesAccordion = renderingAttributes && (
        <ManagedAccordionSection
            title={translate("Attributes")}
            initialExpanded={
                true
                // attributesFirst || (!renderingSpecifications && !renderingDocuments)
            }
            data-test-selector="productDetails_specifications_attributes"
            {...styles.specificationsAccordionSection}
        >
            <StyledWrapper {...styles.tablesWrapper}>
                <StyledWrapper {...styles.tableContainer}>
                    <DataTable {...styles.table} useDesktopViewForMobile={true}>
                        {/* xng-change */}
                        <DataTableBody>
                            {attributesFirstTable}
                            {isTabletScreen &&
                                attributesSecondTable &&
                                attributesSecondTable.length > 0 &&
                                attributesSecondTable}
                        </DataTableBody>
                    </DataTable>
                </StyledWrapper>

                {/* xng-change */}
                {attributesSecondTable && attributesSecondTable.length > 0 && !isTabletScreen && (
                    <StyledWrapper {...styles.tableContainer} {...{ startPositionIsOdd: attrFirstTableLengthIsOdd }}>
                        <DataTable {...styles.table} useDesktopViewForMobile={true}>
                            <DataTableBody>{attributesSecondTable}</DataTableBody>
                        </DataTable>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </ManagedAccordionSection>
    );

    const specificationsFirstTable: any[] = [];
    const specificationsSecondTable: any[] = [];
    renderingSpecifications &&
        specifications.forEach((specification, index) => {
            if (index % 2) {
                // odd - push on second table
                specificationsSecondTable.push(
                    <DataTableRow key={specification.id.toString()}>
                        <DataTableCell {...styles.labelCell}>
                            <Typography {...styles.label}>{specification.nameDisplay}</Typography>
                        </DataTableCell>
                        <DataTableCell {...styles.valueCell}>
                            <Typography {...styles.value}>
                                {parse(transformContentHtml(specification.htmlContent), parserOptions)}
                            </Typography>
                        </DataTableCell>
                    </DataTableRow>,
                );
            } else {
                // even - push on first table
                specificationsFirstTable.push(
                    <DataTableRow key={specification.id.toString()}>
                        <DataTableCell {...styles.labelCell}>
                            <Typography {...styles.label}>{specification.nameDisplay}</Typography>
                        </DataTableCell>
                        <DataTableCell {...styles.valueCell}>
                            <Typography {...styles.value}>
                                {parse(transformContentHtml(specification.htmlContent), parserOptions)}
                            </Typography>
                        </DataTableCell>
                    </DataTableRow>,
                );
            }
        });
    const firstTableLengthIsOdd =
        specificationsFirstTable && specificationsFirstTable.length && specificationsFirstTable.length % 2;

    return (
        <Accordion
            headingLevel={2}
            data-test-selector="productDetails_specifications"
            {...styles.specificationsAccordion}
        >
            {attributesFirst && attributesAccordion}
            {/* XNG-Changes */}
            <ProductDetailsVimeo isExpandedByDefault={isVideoExpandedByDefault} />
            {documentsFirst && documentsAccordion}
            {/* XNG-Change */}
            {renderingDescription && descriptionAccordion}
            {renderingSpecifications && (
                <ManagedAccordionSection
                    initialExpanded={
                        true
                        // (!documentsFirst || !renderingDocuments) &&
                        // (!attributesFirst || !renderingAttributes) &&
                        // !renderingDescription
                    }
                    title={translate("Specification")}
                    data-test-selector="productDetails_specifications_specification"
                    {...styles.specificationsAccordionSection}
                >
                    <StyledWrapper {...styles.tablesWrapper}>
                        <StyledWrapper {...styles.tableContainer}>
                            <DataTable {...styles.table} useDesktopViewForMobile={true}>
                                <DataTableBody>{specificationsFirstTable}</DataTableBody>
                            </DataTable>
                        </StyledWrapper>

                        {/* XNG-Changes */}
                        {specificationsSecondTable && specificationsSecondTable.length > 0 && (
                            <StyledWrapper
                                {...styles.tableContainer}
                                {...{ startPositionIsOdd: firstTableLengthIsOdd }}
                            >
                                <DataTable {...styles.table} useDesktopViewForMobile={true}>
                                    <DataTableBody>{specificationsSecondTable}</DataTableBody>
                                </DataTable>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </ManagedAccordionSection>
            )}
            {!attributesFirst && attributesAccordion}
            {!documentsFirst && documentsAccordion}
        </Accordion>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsSpecifications),
    definition: {
        displayName: "Specification",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.showDocuments,
                displayName: "Show Documents",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.documentsPosition,
                displayName: "Documents Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "first",
                horizontal: true,
                fieldType: "General",
                sortOrder: 2,
                options: [
                    {
                        displayName: "Display First",
                        value: "first",
                    },
                    {
                        displayName: "Display Last",
                        value: "last",
                    },
                ],
                isVisible: widget => widget.fields.showDocuments,
            },
            {
                name: fields.showAttributes,
                displayName: "Show Attributes",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.attributesPosition,
                displayName: "Attributes Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "first",
                horizontal: true,
                fieldType: "General",
                sortOrder: 4,
                options: [
                    {
                        displayName: "Display First",
                        value: "first",
                    },
                    {
                        displayName: "Display Last",
                        value: "last",
                    },
                ],
                isVisible: widget => widget.fields.showAttributes,
            },
            // XNG-Changes
            {
                name: fields.isVideoExpandedByDefault,
                displayName: "Is Video Expanded By Default",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 5,
            },
        ],
    },
};

export default widgetModule;
